// Detects that the page was machine-translated by the browser / a translation proxy.
// Translators rewrite text nodes (e.g. Google wraps them in <font>), which breaks React reconciliation
// ("Failed to execute 'removeChild' on 'Node'"), so this info is attached to JS error reports.
// Keep in sync with apps/promo/helpers/translationInfo.ts

export type TranslationService = 'google-proxy' | 'google' | 'microsoft' | 'yandex' | 'firefox';

export interface TranslationInfo {
    service: TranslationService;
    from: string | null;
    to: string | null;
}

export const UNKNOWN_LANG = 'unknown';

const GOOGLE_PROXY_HOST_SUFFIX = '.translate.goog';
const GOOGLE_SELECTOR = '#goog-gt-tt, link[href^="https://translate.googleapis.com"]';
const MICROSOFT_SELECTOR = '[_msthash], [_msttexthash]';
const YANDEX_SELECTOR = 'ya-tr-span';
const FIREFOX_SELECTOR = '[x-bergamot-translated]';

export const getTranslationInfo = (): TranslationInfo | null => {
    try {
        if (typeof window === 'undefined' || typeof document === 'undefined') {
            return null;
        }

        const html = document.documentElement;
        const lang = html.getAttribute('lang') || null;
        const origLang = html.dataset.origLang || null;

        if (window.location.hostname.endsWith(GOOGLE_PROXY_HOST_SUFFIX)) {
            const params = new URLSearchParams(window.location.search);
            return {
                service: 'google-proxy',
                from: params.get('_x_tr_sl') || origLang,
                to: params.get('_x_tr_tl') || UNKNOWN_LANG,
            };
        }

        if (html.classList.contains('translated-ltr') || html.classList.contains('translated-rtl') || document.querySelector(GOOGLE_SELECTOR)) {
            return { service: 'google', from: origLang, to: lang };
        }

        // These translators do not reliably update <html lang>, so an unchanged lang means "target unknown"
        const to = lang && lang !== origLang ? lang : UNKNOWN_LANG;

        if (document.querySelector(MICROSOFT_SELECTOR)) {
            return { service: 'microsoft', from: origLang, to };
        }
        if (document.querySelector(YANDEX_SELECTOR)) {
            return { service: 'yandex', from: origLang, to };
        }
        if (document.querySelector(FIREFOX_SELECTOR)) {
            return { service: 'firefox', from: origLang, to };
        }

        return null;
    } catch {
        return null;
    }
};
